import {TextObject} from "./selectableObjects";
import {EventVerteiler} from "../global/eventVerteiler";
import {SelObController} from "./controllerModules/selObController";
import {valuesAreEqualWithinPrecision} from "../global/globalUtils";
import {PartSelectableObjectText} from "./partSelObText";
import {OutlineInfoProvider} from "./owayoJSViewerWithSelObs";
import {autocurry} from "../global/functionalTools";
import globalsObject from "./globalsObject";
import {logIfDebug} from "../global/logFunctions";

export enum OUTLINELEVELS {
    MIN,
    SMALL,
    OPTIMAL,
    BIG,
    MAX
}



export class Textoutline {
    public static outlineInfoProvider: OutlineInfoProvider|null = null;

    private static EQUATIONS = {
        MIN: {m: 0.05, t: 0.74},
        OPTIMAL_WIDTH_UNDER5CM: {m: 0.23, t: 0.65},
        OPTIMAL_WIDTH_OVER5CM: {m: 0.17, t: 0.96},
        MAX: {m: 0.29, t: 1.16}
    };

    public static init(outlineInfoProvider: OutlineInfoProvider|null = null) {
        if (Textoutline.outlineInfoProvider !== null) {
            return;
        }
        if (outlineInfoProvider === null) {
            Textoutline.outlineInfoProvider = globalsObject.owayoJSViewer;
            return;
        }
        Textoutline.outlineInfoProvider = outlineInfoProvider as OutlineInfoProvider;

    }

    public static getOutlineHeightInCM(textHeightCM, outlineWidthLevel) {
        switch (outlineWidthLevel) {
            case OUTLINELEVELS.MIN:
                return Textoutline.getMINOutlineWidth(textHeightCM);
            case OUTLINELEVELS.MAX:
                return Textoutline.getMAXOutlineWidth(textHeightCM);
            case OUTLINELEVELS.SMALL:
                return Textoutline.getSMALLOutlineWidth(textHeightCM);
            case OUTLINELEVELS.BIG:
                return Textoutline.getBIGOutlineWidth(textHeightCM);
            case OUTLINELEVELS.OPTIMAL:
            default:
                return Textoutline.getOptimalOutlineWidth(textHeightCM);
        }
    }

    private static getHeightInCMFromPixelheight(partPixelHeight, partCMHeight, pixelHeightInclScaling) {
        return pixelHeightInclScaling * partCMHeight / partPixelHeight;
    }

    static getNearestOutlineLevel(selectableObject: TextObject):OUTLINELEVELS {
        const effectiveHeight = Textoutline.getEffectiveHeightCMForOutlineComputation(selectableObject);
        const outlineHeightCM = Textoutline.getHeightInCMFromPixelheight(
            Textoutline.getSelObParentHeightInPixel(selectableObject),
            Textoutline.getSelObParentHeightInCM(selectableObject),
            selectableObject.outlineWidth * Textoutline.getSelObScalingY(selectableObject)
        );
        return [OUTLINELEVELS.MIN, OUTLINELEVELS.SMALL, OUTLINELEVELS.OPTIMAL, OUTLINELEVELS.BIG, OUTLINELEVELS.MAX]
            .reduce(
                function (nearestLevelWithDelta, level) {
                    const delta = Math.abs(Textoutline.getOutlineHeightInCM(effectiveHeight, level) - outlineHeightCM);
                    if (delta < nearestLevelWithDelta.delta) {
                        nearestLevelWithDelta.level = level;
                        nearestLevelWithDelta.delta = delta;
                    }
                    return nearestLevelWithDelta;
                },
                {level: OUTLINELEVELS.MIN, delta: Infinity}
            ).level;
    }

    private static getSelObScalingY(selOb:TextObject) {
        Textoutline.init();
        return (Textoutline.outlineInfoProvider as OutlineInfoProvider).getSelObScalingY(selOb.id);
    }

    private static getSelObParentHeightInPixel(selOb:TextObject) {
        Textoutline.init();
        return (Textoutline.outlineInfoProvider as OutlineInfoProvider).getSelObParentHeightInPixel(selOb.id);
    }

    private static getSelObParentHeightInCM(selOb:TextObject) {
        return (Textoutline.outlineInfoProvider as OutlineInfoProvider).getSelObParentHeightInCM(selOb.id);
    }

    public static outlineLevelIsMinimalOrSmaller(textHeight, outlineWidthCM) {
        const outlineWidth = Textoutline.getOutlineHeightInCM(textHeight, OUTLINELEVELS.MIN);
        return outlineWidth > outlineWidthCM || valuesAreEqualWithinPrecision(outlineWidthCM, outlineWidth, 0.001);
    }

    public static outlineLevelIsMaxOrBigger(textHeight, outlineWidthCM) {
        const outlineWidth = Textoutline.getOutlineHeightInCM(textHeight, OUTLINELEVELS.MAX);
        return outlineWidth < outlineWidthCM || valuesAreEqualWithinPrecision(outlineWidthCM, outlineWidth);
    }

    public static getChangedOutlineWidthCM(textOutlineLevel: OUTLINELEVELS, effectiveTextHeight) {
        return Textoutline.getOutlineHeightInCM(
            effectiveTextHeight,
            textOutlineLevel
        );
    }

    private static interpolate = autocurry((func1, func2, textHeight) => (func1(textHeight) + func2(textHeight)) / 2);
    private static getOutlineWidthForStartingValueAndSlope = autocurry((startingValue, slope, textHeight) => (startingValue + textHeight * slope) / 5); // *2, da die outline zur Hälfte aussen ist, /10, da das Millimeter ergibt

    private static getMINOutlineWidth = Textoutline.getOutlineWidthForStartingValueAndSlope(Textoutline.EQUATIONS.MIN.t, Textoutline.EQUATIONS.MIN.m);
    private static getMAXOutlineWidth = Textoutline.getOutlineWidthForStartingValueAndSlope(Textoutline.EQUATIONS.MAX.t, Textoutline.EQUATIONS.MAX.m);
    private static getOptimalOutlineOver5cmWidth = Textoutline.getOutlineWidthForStartingValueAndSlope(Textoutline.EQUATIONS.OPTIMAL_WIDTH_OVER5CM.t, Textoutline.EQUATIONS.OPTIMAL_WIDTH_OVER5CM.m);
    private static getOptimalOutlineUnder5cmWidth = Textoutline.getOutlineWidthForStartingValueAndSlope(Textoutline.EQUATIONS.OPTIMAL_WIDTH_UNDER5CM.t, Textoutline.EQUATIONS.OPTIMAL_WIDTH_UNDER5CM.m);

    private static getOptimalOutlineWidth = textHeight => textHeight >= 5 ?
        Textoutline.getOptimalOutlineOver5cmWidth(textHeight) :
        Textoutline.getOptimalOutlineUnder5cmWidth(textHeight);

    private static getBIGOutlineWidth = Textoutline.interpolate(Textoutline.getOptimalOutlineWidth, Textoutline.getMAXOutlineWidth);
    private static getSMALLOutlineWidth = Textoutline.interpolate(Textoutline.getOptimalOutlineWidth, Textoutline.getMINOutlineWidth);

    public static textHasDescenders = text => text.match("[gjpqy]") !== null;
    public static textHasAscenders = text => text.match("[A-Z0-9bdfhijklt]") !== null;


    public static getTextHeightModifierByAscendersAndDescendersInText(textInhalt) {

        const hasDescenders = Textoutline.textHasDescenders(textInhalt);
        const hasAscenders = Textoutline.textHasAscenders(textInhalt);

        if (hasAscenders && !hasDescenders) {
            return 1;
        }
        if (!hasAscenders && !hasDescenders) {
            return 1.32;
        }
        if (!hasAscenders && hasDescenders) {
            return 0.94;
        }
        // if (hasAscenders && hasDescenders) {
            return 0.77
        // }
    }

    public static getNextLowerOutlineLevel = currentLevel => isNaN(currentLevel) ? OUTLINELEVELS.OPTIMAL : currentLevel === OUTLINELEVELS.MIN ? currentLevel : currentLevel - 1;
    public static getNextHigherOutlineLevel = currentLevel => isNaN(currentLevel) ? OUTLINELEVELS.OPTIMAL : currentLevel === OUTLINELEVELS.MAX ? currentLevel : currentLevel + 1;

    private static sendOutlineButtonDisabledHint(textObject, messageIfSingleButtonDisabled) {
        if (textObject.outlineColorValue === "<none>") {
            EventVerteiler.broadcast("displayHintEvent", "konf_noOutlineSelected");
            return;
        }
        EventVerteiler.broadcast("displayHintEvent", messageIfSingleButtonDisabled);
        return;
    }

    public static initOutlinePlusButton(controller: SelObController, domElement, textObject: TextObject) {
        Textoutline.init();
        domElement.off().click(function (event) {
            if ($(event.currentTarget).hasClass("disabled")) {
                Textoutline.sendOutlineButtonDisabledHint(textObject, "konf_maxOutlineWidthReached");
                return;
            }
            textObject.outlineWidthLevel = Textoutline.getNextHigherOutlineLevel(textObject.outlineWidthLevel);
            const newOutlineWidth = Textoutline.getHeightInPixelsFromHeightInCMRespectingScaling(
                textObject.id,
                textObject.outlineWidthLevel,
                Textoutline.getEffectiveHeightCMForOutlineComputation(textObject)
            );

            controller.changeSelOb(textObject.id, {outlineWidth: newOutlineWidth}, false);
        });
    }

    public static initOutlineMinusButton(controller: SelObController, domElement, textObject: TextObject) {
        domElement.off().click(function (event) {
            if ($(event.currentTarget).hasClass("disabled")) {
                Textoutline.sendOutlineButtonDisabledHint(textObject, "konf_minOutlineWidthReached");
                return;
            }
            textObject.outlineWidthLevel = Textoutline.getNextLowerOutlineLevel(textObject.outlineWidthLevel);
            logIfDebug("new Level" + textObject.outlineWidthLevel);
            const textHeight = Textoutline.getEffectiveHeightCMForOutlineComputation(textObject);
            Textoutline.init();
            const newOutlineWidthInPixel = Textoutline.getHeightInPixelsFromHeightInCMRespectingScaling(
                textObject.id,
                textObject.outlineWidthLevel, textHeight
            );
            controller.changeSelOb(textObject.id, {outlineWidth: newOutlineWidthInPixel}, false);
        });
    }

    public static getHeightInPixelsFromHeightInCMRespectingScaling(id,outlineWidthLevel, textHeight) {
        Textoutline.init();

        return (Textoutline.outlineInfoProvider as OutlineInfoProvider).getHeightInPixelsFromHeightInCMRespectingScaling(
            id,
            Textoutline.getChangedOutlineWidthCM(outlineWidthLevel, textHeight)
        );
    }

    public static getPartSelOb(id:string):PartSelectableObjectText {
        Textoutline.init();
        return (Textoutline.outlineInfoProvider as OutlineInfoProvider).getPartSelObById(id) as PartSelectableObjectText;
    }

    public static getEffectiveHeightCMForOutlineComputation(text: TextObject) {
        const partSelOb = Textoutline.getPartSelOb(text.id);
        const gesamtGroesseCM = (partSelOb as PartSelectableObjectText).getHeigtInCMFromLocalCanvas();
        const outlineHoehePixel = text.outlineWidth * partSelOb.scaling.y;
        const outlineHoeheCM = Math.floor(outlineHoehePixel) / partSelOb.getParent().textureSize.y * partSelOb.getParent().partSizeCM.y;
        const gesamtGroesseCMOhneOutline = gesamtGroesseCM - outlineHoeheCM;
        return gesamtGroesseCMOhneOutline * Textoutline.getTextHeightModifierByAscendersAndDescendersInText(text.value);
    }

    public static getOutlineWidthInPixelsFromPartSelOb(textObject: TextObject) {
        return globalsObject.owayoJSViewer.getHeightInPixelsFromHeightInCMRespectingScaling(
            textObject.id,
            Textoutline.getOutlineHeightInCM(
                Textoutline.getEffectiveHeightCMForOutlineComputation(textObject),
                textObject.outlineWidthLevel
            )
        );
    }
}
